import { useEffect, useState } from "react";
import { Row, Col, Card, Spinner } from "react-bootstrap";
import { formatDate } from "../../../utility/Formatdate";
import { Doughnut, Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import "../../../styles/typography.css";
import "../../Dashboard/styles/dashboard.css";
import SimpleTable from "../../../components/atoms/table/simpleTable/simpleTable";
import CompletedIcon from "../../../components/Assests/Icons/Completed.png";
import TotalRequestIcon from "../../../components/Assests/Icons/TotalRequest.png";
import Button from "../../../components/atoms/button/Button";
import DateRangePicker from "../../../components/atoms/daterangePicker/DateRangePicker";
import CustomerSecondNav from "../components/CustomerSecondNav";
import { getCustomerDashboardData } from "./customerDashboardApi";
import { useNavigate } from "react-router-dom";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

// Utility: generate consistent colors
const generateColor = (index) => {
  const palette = [
    "#FEB45D", "#5091DD", "#128b8bff", "#9B59B6",
    "#e49311ff", "#1ABC9C", "#E74C3C", "#2ECC71",
    "#34495E", "#9e4205ff", "#086956ff", "#5627aeff"
  ];
  return palette[index % palette.length];
};

const CARD_HEIGHT = 280;

const CustomerDashboard = () => {
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);

  const [fromDate, setFromDate] = useState(oneMonthAgo);
  const [toDate, setToDate] = useState(today);
  const [dashboardData, setDashboardData] = useState({
    sampleCount: { monthlyCounts: [], period: "" },
    drugsRatio: { ratios: [] },
    caseStatus: { statuses: [] },
    requestSummary: { totalRequest: 0, totalCompleted: 0 },
    recentSampleRequests: [],
  });
  const [period, setPeriod] = useState(6);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleSearch = async () => {
    try {
      const {userId }= JSON.parse(localStorage.getItem("userData")) || 0;
      const payload = {
        userId,
        period,
        startDate: fromDate ? new Date(fromDate).toISOString() : new Date().toISOString(),
        endDate: toDate ? new Date(toDate).toISOString() : new Date().toISOString(),
      };
      const data = await getCustomerDashboardData(payload);
      setDashboardData(data);
    } catch (error) {
      console.error("Error fetching Customer Dashboard data:", error);
    }
  };

  const handlePeriodChange = (e) => {
    setPeriod(Number(e.target.value));
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const userId = userData.userID || 0;
        const today = new Date();
        let startDate, endDate;
        if (period === 6) {
          startDate = new Date(today.getFullYear(), today.getMonth() - 5, 1);
          endDate = today;
        } else {
          startDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
          endDate = today;
        }
        const payload = {
          userId,
          period,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        };
        const data = await getCustomerDashboardData(payload);
        setDashboardData(data);
      } catch (error) {
        console.error("Error fetching Customer Dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [period]);

  // ===== Dynamic Drugs Ratio =====
  const drugsFromAPI = dashboardData.drugsRatio?.ratios || [];
  const DRUGS_DYNAMIC = drugsFromAPI.map((drug, index) => ({
    label: drug.drugName,
    color: generateColor(index),
    percentage: drug.percentage,
  }));

  const drugsChartData = {
    labels: DRUGS_DYNAMIC.map((d) => d.label),
    datasets: [
      {
        data: DRUGS_DYNAMIC.map((d) => d.percentage || 0),
        backgroundColor: DRUGS_DYNAMIC.map((d) => d.color),
        borderWidth: 1,
      },
    ],
  };

  // ===== Sample Count Bar Chart =====
  const monthlyCounts = dashboardData.sampleCount.monthlyCounts || [];
  const sampleCountLabels = monthlyCounts.map((item) => item.month);
  const sampleCountData = monthlyCounts.map((item) => item.count);

  const barChartData = {
    labels: sampleCountLabels,
    datasets: [
      {
        label: "Sample count",
        data: sampleCountData,
        backgroundColor: "#3498db",
        borderRadius: 0,
        barPercentage: 0.6,
        categoryPercentage: 0.6,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 13 } } },
      y: {
        beginAtZero: true,
        grid: { color: "#eee" },
        ticks: { stepSize: 10, font: { size: 13 } },
      },
    },
  };

  // ===== Recent Sample Requests Table =====
  const sampleStatusColors = {};
  dashboardData.caseStatus?.statuses?.forEach((status, i) => {
    sampleStatusColors[status.status] = generateColor(i);
  });

  const recentSampleColumns = [
    { header: "Date", accessor: "date" },
    { header: "Group ID", accessor: "groupId" },
    { header: "Barcode No.", accessor: "barcodeNo" },
    { header: "Species", accessor: "species" },
    { header: "Product", accessor: "product" },
    {
      header: "Status",
      accessor: "status",
      disableSort: true,
      cell: (sample) => (
        <span
          style={{
            background: sampleStatusColors[sample.status] || "#eee",
            color: "#333",
            borderRadius: 6,
            padding: "2px 12px",
            fontWeight: 500,
            fontSize: 13,
          }}
        >
          {sample.status}
        </span>
      ),
    },
  ];

  // ===== Case Status Pie Chart =====
  const statusesFromAPI = dashboardData.caseStatus?.statuses || [];
  const CASE_STATUS_DYNAMIC = statusesFromAPI.map((s, i) => ({
    label: s.status,
    color: generateColor(i),
    count: s.count,
  }));

  const pieChartData = {
    labels: CASE_STATUS_DYNAMIC.map((s) => s.label),
    datasets: [
      {
        data: CASE_STATUS_DYNAMIC.map((s) => s.count || 0),
        backgroundColor: CASE_STATUS_DYNAMIC.map((s) => s.color),
        borderWidth: 1,
      },
    ],
  };

  const pieChartOptions = {
    plugins: { legend: { display: false } },
    responsive: true,
    maintainAspectRatio: false,
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0" }}>
     <Spinner animation="border" /> Loading...
      </div>
    );
  }

  return (
    <div>
      <CustomerSecondNav />
      <div className="dashboard-header default-font-styles">
        <div className=" d-flex align-items-center default-font-styles">
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onFromDateChange={(date) => setFromDate(date)}
            onToDateChange={(date) => setToDate(date)}
            fromLabel="From"
            toLabel="To"
            className="me-3"
          />
          <Button
            variant="outlined"
            size="md"
            className="default-font-styles"
            style={{ minWidth: 76, height: 44 }}
            onClick={handleSearch}
            title="Search dashboard data"
          >
            Search
          </Button>
        </div>
      </div>

      <div style={{ padding: "0 1px" }}>
        <Row className="mb-4 g-3">
          {/* Sample Count */}
          <Col xs={12} md={6} lg={3}>
            <Card className="shadow-sm mb-0 default-font-styles" style={{ height: CARD_HEIGHT }}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <Card.Title className="mb-0 customer-default-style">Sample count</Card.Title>
                  <div className="mb-0" style={{ }}>
                    <select
                      value={period}
                      onChange={handlePeriodChange}
                      className="default-font-styles"
                      title="Select time period"
                      style={{ fontSize: 13, color: "#333", border: "1px solid #e0e0e0", borderRadius: 6, padding: "2px 8px", background: "#fff", outline: "none", width: "100%" }}
                    >
                      <option value={6}>Last 6 Months</option>
                      <option value={1}>Last 1 Year</option>
                    </select>
                  </div>
                </div>
                <div style={{ width: "100%",overflowX: "auto", marginTop: 8 }}>
                  <Bar data={barChartData} options={barChartOptions} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Drugs Ratio */}
          <Col xs={12} md={6} lg={3}>
            <Card className="shadow-sm mb-0 customer-subtitle-style" style={{ height: CARD_HEIGHT }}>
              <Card.Body>
                <Card.Title className="customer-default-style">Drugs Ratio</Card.Title>
                <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Doughnut data={drugsChartData} options={{ cutout: "70%", plugins: { legend: { display: false } } }} />
                </div>
                <div className="mt-2 customer-subtitle-style">
                  {DRUGS_DYNAMIC.map((d) => (
                    <span key={d.label} style={{ marginRight: 8 }}>
                      <span style={{ color: d.color }}>■</span>
                      <span style={{ color: "#0B1C33", marginLeft: 4 }}>{d.label}</span>
                    </span>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Case Status */}
          <Col xs={12} md={6} lg={3}>
            <Card className="shadow-sm mb-0 customer-subtitle-style" style={{ height: CARD_HEIGHT }}>
              <Card.Body>
                <Card.Title className="customer-default-style">Case Status</Card.Title>
                <div style={{ height: 105, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Pie data={pieChartData} options={pieChartOptions} />
                </div>
                <div className="mt-2 customer-subtitle-style">
                  {CASE_STATUS_DYNAMIC.map((s) => (
                    <span key={s.label} style={{ marginRight: 8 }}>
                      <span style={{ color: s.color }}>■</span>
                      <span style={{ color: "#0B1C33", marginLeft: 4 }}>{s.label}</span>
                    </span>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Requests Summary */}
          <Col xs={12} md={6} lg={3}>
            <Card className="shadow-sm default-font-styles" style={{ minHeight: 280 }}>
              <Card.Body className="d-flex flex-column justify-content-center align-items-center p-4">
                <div className="d-flex flex-column align-items-center w-100">
                  <div className="d-flex align-items-center mb-3 w-100">
                    <img src={TotalRequestIcon} alt="Total Request" style={{ width: 54, height: 54, marginRight: 12 }} />
                    <div>
                      <div style={{ fontSize: 40, fontWeight: 400, color: "#003087", lineHeight: 1 }}>{dashboardData.requestSummary.totalRequest}</div>
                      <div style={{ fontSize: 16, color: "#888" }}>Total Request</div>
                    </div>
                  </div>
                  <hr style={{ width: "60%", margin: "12px 0" }} />
                  <div className="d-flex align-items-center w-100">
                    <img src={CompletedIcon} alt="Completed" style={{ width: 54, height: 54, marginRight: 12 }} />
                    <div>
                      <div style={{ fontSize: 40, fontWeight: 400, color: "#003087", lineHeight: 1 }}>{dashboardData.requestSummary.totalCompleted}</div>
                      <div style={{ fontSize: 16, color: "#888" }}>Total Completed</div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Recent Sample Requests */}
        <Card className="shadow-sm default-font-styles">
          <Card.Body>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-2">
              <span className="customer-default-style">Recent Sample Request</span>
              <Button variant="outlined" size="sm" className="mt-2 mt-md-0 default-font-styles" style={{ minWidth: 112, fontWeight: 500, boxShadow: "none", padding: "9px 20px" }} onClick={() => navigate("/assay-request-record")} title="View all sample requests">
                View All
              </Button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <SimpleTable
                columns={recentSampleColumns}
                data={dashboardData.recentSampleRequests.map((sample) => ({ ...sample, date: sample.date ? formatDate(sample.date) : "-" }))}
                showPagination={false}
                className="default-font-styles"
              />
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default CustomerDashboard;
